/**
 * Pexels image search adapter. Normalized interface for the unified image picker.
 * Extracted from pexels-search.js — same API logic, no modal UI.
 */

import { getStoredValue, setStoredValue } from './api-key-storage.js';

const API_URL = 'https://api.pexels.com/v1/search';
const PER_PAGE = 30;
const SEARCH_TIMEOUT_MS = 10_000;
const DOWNLOAD_TIMEOUT_MS = 15_000;
const LS_KEY = 'pb-pexels-api-key';

const MIME_TO_EXT = {
  'image/jpeg': '.jpg',
  'image/png': '.png',
  'image/webp': '.webp',
  'image/gif': '.gif',
  'image/avif': '.avif'
};

export const pexels = {
  name: 'Pexels',
  needsKey: true,
  lsKey: LS_KEY,
  attribution: 'Photos provided by Pexels',
  keyHelp: 'Get a free key at pexels.com/api',

  getApiKey() {
    return getStoredValue(LS_KEY);
  },

  setApiKey(key) {
    setStoredValue(LS_KEY, key);
  },

  async search(query, page, signal) {
    const key = this.getApiKey();
    if (!key) throw Object.assign(new Error('No API key'), { code: 'NO_KEY' });

    const url = `${API_URL}?query=${encodeURIComponent(query)}&per_page=${PER_PAGE}&page=${page}`;
    const combined = signal
      ? AbortSignal.any([signal, AbortSignal.timeout(SEARCH_TIMEOUT_MS)])
      : AbortSignal.timeout(SEARCH_TIMEOUT_MS);

    const resp = await fetch(url, {
      headers: { Authorization: key },
      signal: combined
    });

    if (resp.status === 401) throw Object.assign(new Error('Invalid API key'), { code: 'AUTH' });
    if (resp.status === 429) throw Object.assign(new Error('Rate limited'), { code: 'RATE' });
    if (!resp.ok) throw new Error(`Pexels API error: ${resp.status}`);

    const data = await resp.json();

    return {
      results: data.photos.map((p) => ({
        id: String(p.id),
        thumb: p.src.medium,
        title: p.alt || 'Stock photo',
        author: p.photographer,
        authorUrl: p.photographer_url,
        _raw: p
      })),
      total: data.total_results
    };
  },

  async download(item) {
    const photo = item._raw;
    const resp = await fetch(photo.src.large, {
      signal: AbortSignal.timeout(DOWNLOAD_TIMEOUT_MS)
    });
    if (!resp.ok) throw new Error(`Download failed: ${resp.status}`);

    const blob = await resp.blob();
    const mime = blob.type || resp.headers.get('Content-Type') || 'image/jpeg';
    const ext = MIME_TO_EXT[mime] || '.jpg';
    return new File([blob], `pexels-${photo.id}${ext}`, { type: mime });
  }
};
